import { useCallback, useEffect, useMemo, useState } from "react";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** SessionStorage key for preserving the user's path across version redirects. */
const VERSION_RETURN_PATH_KEY = "version_return_path";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Describes a detected version mismatch between client and server. */
interface VersionMismatchState {
  hasNewVersion: boolean;
  serverVersion: string;
}

interface UseVersionUpdateOptions {
  /** SessionStorage key used to track a successful version update across page
   *  reloads so a success notification can be shown after the redirect. */
  storageKey?: string;

  /** Subpath within the versioned directory for the entry HTML file.
   *  Defaults to `""` (root) for the main app.
   *  For OnSite, use `"src/mobile-components"`. */
  entryPath?: string;
}

interface UseVersionUpdateReturn {
  /** Whether the version-update banner should be rendered. */
  showBanner: boolean;

  /** Current mismatch details, or `null` when versions match. */
  mismatch: VersionMismatchState | null;

  /** Error message when a version switch fails. */
  error: string | null;

  /** Call when the user clicks "Refresh" / "Update Now". */
  handleAccept: () => void;

  /** Call when the user clicks "Remind later". */
  handleDismiss: () => void;

  /** Clear the error state after showing the error notification. */
  clearError: () => void;

  /**
   * Inspect `serverVersion` returned by the session API.
   *
   * - On initial login (production): verifies the version exists and silently
   *   redirects via window.location.replace(). Preserves the user's URL.
   * - On initial login (DEV): shows banner (can't redirect to versioned builds).
   * - On session refresh: shows the update banner so the user can decide when
   *   to update.
   *
   * @returns `true` if a page redirect was initiated (caller should bail out
   *          of any remaining session-setup logic).
   */
  checkVersion: (
    serverVersion: string | undefined,
    isInitialLogin: boolean
  ) => Promise<boolean>;

  /** The version the user just updated *to* (read from sessionStorage on
   *  mount). `null` if no update just happened. */
  updatedVersion: string | null;

  /** Call after the success notification has been shown to clear the state. */
  clearUpdatedVersion: () => void;
}

// ---------------------------------------------------------------------------
// Pure helpers (no React state – safe to call anywhere)
// ---------------------------------------------------------------------------

/**
 * Compare the server-reported version against the compile-time
 * `__APP_VERSION__`.  Returns the server version when a mismatch is detected,
 * or `null` when versions match (or the server version is empty / unresolved).
 *
 * No format validation is performed here — {@link doesVersionExist} serves as
 * the safety gate by verifying that a matching build actually exists on disk.
 */
const checkVersionMismatch = (
  serverVersion: string | undefined
): string | null => {
  if (!serverVersion || serverVersion.includes("{{")) {
    return null;
  }
  return serverVersion !== __APP_VERSION__ ? serverVersion : null;
};

/**
 * Verify that a versioned build actually exists on the server.
 *
 * Fetches the version's `manifest.json` and verifies its `version` field.
 * A simple HEAD check is unreliable because nginx `try_files` falls back to
 * the root `/index.html` for any missing file, always returning 200.
 */
const doesVersionExist = (version: string): Promise<boolean> => {
  return fetch(`/versions/${version}/manifest.json`)
    .then((resp) => {
      if (!resp.ok) return false;
      return resp.json().then((manifest) => manifest.version === version);
    })
    .catch(() => {
      // JSON parse failure means nginx served the fallback HTML, not the manifest
      return false;
    });
};

/**
 * Perform a version redirect by navigating directly to the versioned index file.
 * Saves the current path/search/hash to sessionStorage so it can be restored
 * after the new version loads (see {@link restorePathAfterVersionRedirect}).
 *
 * @param version  - Target version string (e.g. "2.0.0")
 * @param entryPath - Subpath within the versioned directory for the entry HTML.
 *                    Empty string (default) for main app, `"src/mobile-components"`
 *                    for OnSite.
 */
const performRedirect = (version: string, entryPath = ""): void => {
  // Preserve current location for restoration after the versioned app loads
  const returnPath =
    window.location.pathname + window.location.search + window.location.hash;
  sessionStorage.setItem(VERSION_RETURN_PATH_KEY, returnPath);

  // Build the path: /versions/<version>[/<entryPath>]/index-<version>.html
  const base = entryPath
    ? `/versions/${version}/${entryPath}`
    : `/versions/${version}`;
  window.location.replace(`${base}/index-${version}.html`);
};

/**
 * Restore the original path after a version redirect.
 *
 * **Must be called from entry points (index.tsx) BEFORE React renders**
 * so that BrowserRouter sees the correct path on first render.
 */
export const restorePathAfterVersionRedirect = (): void => {
  const savedPath = sessionStorage.getItem(VERSION_RETURN_PATH_KEY);
  if (savedPath) {
    sessionStorage.removeItem(VERSION_RETURN_PATH_KEY);
    history.replaceState(null, "", savedPath);
  }
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useVersionUpdate(
  options: UseVersionUpdateOptions = {}
): UseVersionUpdateReturn {
  const { storageKey = "version_updated", entryPath = "" } = options;

  const [mismatch, setMismatch] = useState<VersionMismatchState | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [updatedVersion, setUpdatedVersion] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // --- On mount: check if we just completed a version update ---------------
  useEffect(() => {
    const version = sessionStorage.getItem(storageKey);
    if (version) {
      sessionStorage.removeItem(storageKey);
      setUpdatedVersion(version);
    }
  }, [storageKey]);

  // --- Core: check server version and redirect or show banner --------------
  const checkVersion = useCallback(
    (
      serverVersion: string | undefined,
      isInitialLogin: boolean
    ): Promise<boolean> => {
      if (!window.cnc?.settings?.get("multicellUI")) {
        return Promise.resolve(false);
      }

      const mismatchVersion = checkVersionMismatch(serverVersion);
      if (!mismatchVersion) {
        return Promise.resolve(false);
      }

      // In DEV mode, version will never match — skip silently
      if (__APP_VERSION__ === "DEV") {
        return Promise.resolve(false);
      }

      // --- Production: verify version exists then redirect or show banner ---
      return doesVersionExist(mismatchVersion).then((exists) => {
        if (!exists) {
          console.warn(
            `[VersionUpdate] Build for version "${mismatchVersion}" not found — staying on ${__APP_VERSION__}`
          );
          return false;
        }

        // On initial login: auto-redirect silently so the user lands on the
        // correct version without seeing a banner or success toast.
        if (isInitialLogin) {
          console.warn(
            `[VersionUpdate] Switching from ${__APP_VERSION__} to ${mismatchVersion}`
          );
          performRedirect(mismatchVersion, entryPath);
          return true; // redirect initiated — caller should bail
        }

        // Active session (session refresh): show the update banner so the user
        // can decide when to update.
        setDismissed(false);
        setMismatch({ hasNewVersion: true, serverVersion: mismatchVersion });
        return false;
      });
    },
    [entryPath]
  );

  // --- User clicks "Refresh" / "Update Now" --------------------------------
  const handleAccept = useCallback(() => {
    if (!mismatch?.serverVersion) return;

    setError(null);

    // In DEV mode, can't actually redirect — just log and clear
    if (__APP_VERSION__ === "DEV") {
      console.warn(
        `[VersionUpdate] DEV mode — would switch to ${mismatch.serverVersion} in production`
      );
      setMismatch(null);
      return;
    }

    // Verify the version still exists before redirecting
    doesVersionExist(mismatch.serverVersion).then((exists) => {
      if (!exists) {
        console.warn(
          `[VersionUpdate] Build for version "${mismatch.serverVersion}" not found — staying on current version`
        );
        setMismatch(null);
        return;
      }

      // Redirect
      console.warn(
        `[VersionUpdate] Switching from ${__APP_VERSION__} to ${mismatch.serverVersion}`
      );
      sessionStorage.setItem(storageKey, mismatch.serverVersion);
      performRedirect(mismatch.serverVersion, entryPath);
    });
  }, [mismatch, storageKey, entryPath]);

  // --- User clicks "Remind later" ------------------------------------------
  // Banner reappears on next session refresh when checkVersion detects
  // the mismatch again and resets dismissed to false.
  const handleDismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  // --- Derived state -------------------------------------------------------
  const showBanner = useMemo(
    () => mismatch?.hasNewVersion === true && !dismissed,
    [mismatch, dismissed]
  );

  const clearUpdatedVersion = useCallback(() => {
    setUpdatedVersion(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    showBanner,
    mismatch,
    error,
    handleAccept,
    handleDismiss,
    clearError,
    checkVersion,
    updatedVersion,
    clearUpdatedVersion
  };
}
